export type Translate = (
  key: string,
  options?: Record<string, string | number>
) => string;

export type RosterService = {
  name: string;
};

export type RosterScheduleSlot = {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_label?: string | null;
  work_label?: string | null;
};

export const isSpecialtyUnmarked = (
  services: RosterService[] | null | undefined,
  restrictProfessionalServices: boolean
): boolean => {
  if (!restrictProfessionalServices) {
    return false;
  }

  return (services ?? []).every((service) => service.name.trim().length === 0);
};

const SPECIALTY_PREVIEW_COUNT = 3;

/** Monday first, so Seg–Sex groups before the weekend. */
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const wallClock = (value: string): string => value.slice(0, 5);

const dayFollows = (previous: number | undefined, day: number): boolean => {
  if (previous === undefined) {
    return false;
  }

  return DAY_ORDER.indexOf(day) === DAY_ORDER.indexOf(previous) + 1;
};

export const formatSpecialtyLabel = (
  services: RosterService[] | null | undefined,
  restrictProfessionalServices: boolean,
  t: Translate
): string => {
  if (!restrictProfessionalServices) {
    return t("studio.professionals.specialty.allServices");
  }

  const names = (services ?? [])
    .map((service) => service.name.trim())
    .filter((name) => name.length > 0);

  if (names.length === 0) {
    return t("studio.professionals.specialty.noneMarked");
  }

  if (names.length <= SPECIALTY_PREVIEW_COUNT) {
    return names.join(", ");
  }

  return t("studio.professionals.specialty.andMore", {
    names: names.slice(0, SPECIALTY_PREVIEW_COUNT).join(", "),
    count: names.length - SPECIALTY_PREVIEW_COUNT,
  });
};

/** Names left out of “e mais N”, for the hover detail. Null when the full list is already visible. */
export const formatSpecialtyRemainder = (
  services: RosterService[] | null | undefined,
  restrictProfessionalServices: boolean,
  t: Translate
): string | null => {
  if (!restrictProfessionalServices) {
    return null;
  }

  const names = (services ?? [])
    .map((service) => service.name.trim())
    .filter((name) => name.length > 0);

  if (names.length <= SPECIALTY_PREVIEW_COUNT) {
    return null;
  }

  return t("studio.professionals.specialty.also", {
    names: names.slice(SPECIALTY_PREVIEW_COUNT).join(", "),
  });
};

type DayWindow = {
  start: string;
  end: string;
};

type DayGroup = {
  days: number[];
  start: string;
  end: string;
};

export const formatWeeklyRoutineSummary = (
  slots: RosterScheduleSlot[] | null | undefined,
  t: Translate
): string => {
  if (!slots || slots.length === 0) {
    return t("studio.professionals.routine.undefined");
  }

  const byDay = new Map<number, DayWindow>();

  slots.forEach((slot) => {
    const start = wallClock(slot.start_time);
    const end = wallClock(slot.end_time);
    const current = byDay.get(slot.day_of_week);

    if (!current) {
      byDay.set(slot.day_of_week, { start, end });
      return;
    }

    if (start < current.start) {
      current.start = start;
    }

    if (end > current.end) {
      current.end = end;
    }
  });

  const groups: DayGroup[] = [];

  DAY_ORDER.forEach((day) => {
    const window = byDay.get(day);

    if (!window) {
      return;
    }

    const previous = groups[groups.length - 1];
    const previousDay = previous?.days[previous.days.length - 1];
    const sameWindow =
      previous !== undefined &&
      previous.start === window.start &&
      previous.end === window.end &&
      dayFollows(previousDay, day);

    if (sameWindow && previous) {
      previous.days.push(day);
      return;
    }

    groups.push({ days: [day], start: window.start, end: window.end });
  });

  if (groups.length === 1 && groups[0].days.length === 7) {
    return t("studio.professionals.routine.allWeek", {
      start: groups[0].start,
      end: groups[0].end,
    });
  }

  return groups
    .map((group) => {
      const from = t(`studio.professionals.routine.daysShort.${group.days[0]}`);

      if (group.days.length === 1) {
        return t("studio.professionals.routine.single", {
          day: from,
          start: group.start,
          end: group.end,
        });
      }

      const to = t(
        `studio.professionals.routine.daysShort.${group.days[group.days.length - 1]}`
      );

      return t("studio.professionals.routine.range", {
        from,
        to,
        start: group.start,
        end: group.end,
      });
    })
    .join(", ");
};

const daySpan = (days: number[], t: Translate): string => {
  const from = t(`studio.professionals.routine.daysShort.${days[0]}`);

  if (days.length === 1) {
    return from;
  }

  const to = t(`studio.professionals.routine.daysShort.${days[days.length - 1]}`);

  return `${from}–${to}`;
};

/**
 * Mid-day gaps (lunch) as their own line. The working-hours line stays the outer window.
 * Returns null when there is no routine or no gap between windows.
 */
export const formatWeeklyBreakSummary = (
  slots: RosterScheduleSlot[] | null | undefined,
  t: Translate
): string | null => {
  if (!slots || slots.length === 0) {
    return null;
  }

  type BreakWindow = DayWindow & { breakLabel: string };

  const windowsByDay = new Map<number, BreakWindow[]>();

  slots.forEach((slot) => {
    const window = {
      start: wallClock(slot.start_time),
      end: wallClock(slot.end_time),
      breakLabel: slot.break_label?.trim() ?? "",
    };
    const current = windowsByDay.get(slot.day_of_week) ?? [];
    current.push(window);
    windowsByDay.set(slot.day_of_week, current);
  });

  type NamedGap = { range: string; label: string };

  const gapsByDay = new Map<number, NamedGap[]>();

  windowsByDay.forEach((windows, day) => {
    const sorted = [...windows].sort(
      (left, right) =>
        left.start.localeCompare(right.start) || left.end.localeCompare(right.end)
    );
    const gaps: NamedGap[] = [];

    for (let index = 1; index < sorted.length; index += 1) {
      const previousEnd = sorted[index - 1].end;
      const nextStart = sorted[index].start;

      if (previousEnd < nextStart) {
        gaps.push({
          range: `${previousEnd}–${nextStart}`,
          label: sorted[index - 1].breakLabel,
        });
      }
    }

    if (gaps.length > 0) {
      gapsByDay.set(day, gaps);
    }
  });

  if (gapsByDay.size === 0) {
    return null;
  }

  type BreakGroup = {
    days: number[];
    gaps: NamedGap[];
  };

  const gapSignature = (gaps: NamedGap[]): string =>
    gaps.map((gap) => `${gap.range}|${gap.label}`).join(";");

  const groups: BreakGroup[] = [];

  DAY_ORDER.forEach((day) => {
    const dayBreak = gapsByDay.get(day);

    if (!dayBreak) {
      return;
    }

    const previous = groups[groups.length - 1];
    const previousDay = previous?.days[previous.days.length - 1];
    const sameGaps =
      previous !== undefined &&
      gapSignature(previous.gaps) === gapSignature(dayBreak) &&
      dayFollows(previousDay, day);

    if (sameGaps && previous) {
      previous.days.push(day);
      return;
    }

    groups.push({ days: [day], gaps: dayBreak });
  });

  const phraseForGap = (gap: NamedGap): string =>
    gap.label
      ? t("studio.professionals.routine.breakNamed", {
          label: gap.label,
          ranges: gap.range,
        })
      : t("studio.professionals.routine.break", { ranges: gap.range });

  return groups
    .map((group) => {
      const anyNamed = group.gaps.some((gap) => gap.label !== "");
      const detail =
        !anyNamed && group.gaps.length > 1
          ? t("studio.professionals.routine.breaks", {
              ranges: group.gaps.map((gap) => gap.range).join(", "),
            })
          : group.gaps.map(phraseForGap).join(", ");

      return t("studio.professionals.routine.breakWhen", {
        when: daySpan(group.days, t),
        detail,
      });
    })
    .join(", ");
};

/**
 * Named middle work stretches (between pauses), e.g. “Seg Coloração 13:00–15:00”.
 * Returns null when no window has a work_label.
 */
export const formatWeeklyWorkStretchSummary = (
  slots: RosterScheduleSlot[] | null | undefined,
  t: Translate
): string | null => {
  if (!slots || slots.length === 0) {
    return null;
  }

  type NamedStretch = { range: string; label: string };

  const windowsByDay = new Map<
    number,
    Array<{ start: string; end: string; workLabel: string }>
  >();

  slots.forEach((slot) => {
    const window = {
      start: wallClock(slot.start_time),
      end: wallClock(slot.end_time),
      workLabel: slot.work_label?.trim() ?? "",
    };
    const current = windowsByDay.get(slot.day_of_week) ?? [];
    current.push(window);
    windowsByDay.set(slot.day_of_week, current);
  });

  const stretchesByDay = new Map<number, NamedStretch[]>();

  windowsByDay.forEach((windows, day) => {
    const sorted = [...windows].sort(
      (left, right) =>
        left.start.localeCompare(right.start) || left.end.localeCompare(right.end)
    );

    if (sorted.length < 3) {
      return;
    }

    const stretches: NamedStretch[] = [];

    for (let index = 1; index < sorted.length - 1; index += 1) {
      const label = sorted[index].workLabel;
      if (label === "") {
        continue;
      }
      stretches.push({
        range: `${sorted[index].start}–${sorted[index].end}`,
        label,
      });
    }

    if (stretches.length > 0) {
      stretchesByDay.set(day, stretches);
    }
  });

  if (stretchesByDay.size === 0) {
    return null;
  }

  type StretchGroup = { days: number[]; stretches: NamedStretch[] };

  const signature = (items: NamedStretch[]): string =>
    items.map((item) => `${item.range}|${item.label}`).join(";");

  const groups: StretchGroup[] = [];

  DAY_ORDER.forEach((day) => {
    const dayStretches = stretchesByDay.get(day);

    if (!dayStretches) {
      return;
    }

    const previous = groups[groups.length - 1];
    const previousDay = previous?.days[previous.days.length - 1];
    const same =
      previous !== undefined &&
      signature(previous.stretches) === signature(dayStretches) &&
      dayFollows(previousDay, day);

    if (same && previous) {
      previous.days.push(day);
      return;
    }

    groups.push({ days: [day], stretches: dayStretches });
  });

  return groups
    .map((group) => {
      const detail = group.stretches
        .map((stretch) =>
          t("studio.professionals.routine.workNamed", {
            label: stretch.label,
            ranges: stretch.range,
          })
        )
        .join(", ");

      return t("studio.professionals.routine.breakWhen", {
        when: daySpan(group.days, t),
        detail,
      });
    })
    .join(", ");
};
